import { transport, BASE_PATH } from "@/src/config/apiConfig.js";

const getUniboxConfig = () => {
  const env = process.env.NODE_ENV || "development";

  if (env === "production") {
    return {
      baseUrl:
        window.UNIBOX_CONFIG?.endpoint ||
        (window.location.href.includes("in.superagi.com")
          ? "https://unibox.in.superagi.com"
          : "https://unibox.superagi.com"),
    };
  } else if (env === "staging") {
    return {
      baseUrl:
        window.UNIBOX_CONFIG?.endpoint || "https://unibox-staging.superagi.com",
    };
  } else {
    return {
      baseUrl: "http://localhost:8084",
    };
  }
};

const { baseUrl: UNIBOX_BASE_URL } = getUniboxConfig();

const uniboxTransport = {
  async get(url, config = {}) {
    const workspaceId = localStorage.getItem("currentWorkspaceId");
    const userId = config.userId || localStorage.getItem("userId");

    const response = await fetch(`${UNIBOX_BASE_URL}${url}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-WORKSPACE-ID": workspaceId,
        "X-USER-ID": userId,
        ...config.headers,
      },
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch (e) {}
      const error = new Error(`HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.body = errorBody;
      throw error;
    }

    return await response.json();
  },

  async post(url, data, config = {}) {
    const workspaceId = localStorage.getItem("currentWorkspaceId");
    const userId = config.userId || localStorage.getItem("userId");

    const response = await fetch(`${UNIBOX_BASE_URL}${url}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-WORKSPACE-ID": workspaceId,
        "X-USER-ID": userId,
        ...config.headers,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },

  async patch(url, data, config = {}) {
    const workspaceId = localStorage.getItem("currentWorkspaceId");
    const userId = config.userId || localStorage.getItem("userId");

    const response = await fetch(`${UNIBOX_BASE_URL}${url}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-WORKSPACE-ID": workspaceId,
        "X-USER-ID": userId,
        ...config.headers,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },
};

export default {
  async fetchEntities({
    page = 1,
    page_size = 20,
    entity_types = [],
    is_starred = [],
    is_muted = null,
    sort_by = null,
    sort_order = null,
    owner_ids = [],
    message_activity = [],
    search = null,
  }) {
    try {
      const params = new URLSearchParams();

      if (page) params.append("page", page);
      if (page_size) params.append("page_size", page_size);

      // Handle all array parameters as repeated parameters (no [] suffix)
      if (
        entity_types &&
        Array.isArray(entity_types) &&
        entity_types.length > 0
      ) {
        entity_types.forEach((type) => params.append("entity_types", type));
      }

      if (is_starred && Array.isArray(is_starred) && is_starred.length > 0) {
        is_starred.forEach((value) => params.append("is_starred", value));
      }

      if (is_muted !== null && is_muted !== undefined) {
        params.append("is_muted", is_muted);
      }

      if (owner_ids && Array.isArray(owner_ids) && owner_ids.length > 0) {
        owner_ids.forEach((id) => params.append("owner_ids", id));
      }

      if (
        message_activity &&
        Array.isArray(message_activity) &&
        message_activity.length > 0
      ) {
        message_activity.forEach((activity) =>
          params.append("message_activity", activity),
        );
      }

      // Single value parameters
      if (sort_by) params.append("sort_by", sort_by);
      if (sort_order) params.append("sort_order", sort_order);
      if (search) params.append("search", search);

      const queryString = params.toString();
      const url = queryString
        ? `/api/v1/entities?${queryString}`
        : "/api/v1/entities";

      const response = await uniboxTransport.get(url);
      return response.data || response;
    } catch (error) {
      const errorObj = error?.body?.error;
      const isIndexNotFound =
        (typeof errorObj === "string" && errorObj.includes("index_not_found_exception")) ||
        (typeof errorObj === "object" && errorObj?.type === "index_not_found_exception") ||
        (errorObj && JSON.stringify(errorObj).includes("index_not_found_exception"));
      
      if (isIndexNotFound) {
        return { entities: [], total: 0 };
      }
      console.error("Error fetching entities:", error);
      throw error;
    }
  },

  async fetchMessages({
    entity_type,
    entity_id,
    cursor = null,
    seek_direction = null,
    limit = 50,
    channel_type = null,
    direction = null,
  }) {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      if (seek_direction) params.append("seek_direction", seek_direction);
      if (limit) params.append("limit", limit);
      if (channel_type) params.append("channel_type", channel_type);
      if (direction) params.append("direction", direction);

      const queryString = params.toString();
      const url = queryString
        ? `/api/v1/entities/${entity_type}/${entity_id}/messages?${queryString}`
        : `/api/v1/entities/${entity_type}/${entity_id}/messages`;

      const response = await uniboxTransport.get(url);
      return response.data || response;
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw error;
    }
  },

  async sendWhatsAppText({
    entity_id,
    entity_type,
    phone_number,
    message_text,
    whatsapp_config_id,
    attachments = [],
  }) {
    try {
      const workspaceId = localStorage.getItem("currentWorkspaceId");
      const payload = {
        workspace_id: workspaceId,
        entity_id,
        entity_type,
        phone_number,
        message_text,
        whatsapp_config_id,
      };

      if (attachments && attachments.length > 0) {
        payload.attachments = attachments;
      }

      const response = await transport.post(
        `${BASE_PATH}/api/unibox/whatsapp/send_text`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error sending WhatsApp message:", error);
      throw error;
    }
  },

  async fetchWhatsAppConfigs(only_connected = true) {
    try {
      const params = new URLSearchParams();
      if (only_connected !== undefined) {
        params.append("only_connected", only_connected);
      }

      const queryString = params.toString();
      const url = queryString
        ? `${BASE_PATH}/api/marketing/whatsapp/configs?${queryString}`
        : `${BASE_PATH}/api/marketing/whatsapp/configs`;

      const response = await transport.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching WhatsApp configs:", error);
      throw error;
    }
  },

  async updateEntity(entity_type, entity_id, data) {
    try {
      const response = await uniboxTransport.patch(
        `/api/v1/entities/${entity_type}/${entity_id}`,
        data,
      );
      return response.data || response;
    } catch (error) {
      console.error("Error updating entity:", error);
      throw error;
    }
  },

  async fetchViews() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/unibox/views`);
      return response.data;
    } catch (error) {
      console.error("Error fetching views:", error);
      throw error;
    }
  },

  async fetchViewSharingDetails(view_id) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/views/${view_id}/sharing`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view sharing details:", error);
      throw error;
    }
  },

  async createView(view_data) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/views`, {
        entity_type: "unibox",
        name: view_data.name,
        sharing_scope:
          view_data.sharing_scope || view_data.access_level || "personal",
        team_ids: view_data.team_ids || [],
        user_ids: view_data.user_ids || [],
        filters: view_data.filters || {},
        sort: view_data.sort || [],
        view_type: view_data.view_type || "table",
        group_by_config: view_data.group_by_config || null,
      });
      return response.data;
    } catch (error) {
      console.error("Error creating view:", error);
      throw error;
    }
  },

  async updateView(view_id, view_data) {
    try {
      const response = await transport.put(
        `${BASE_PATH}/api/views/${view_id}`,
        view_data,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating view:", error);
      throw error;
    }
  },

  async deleteView(view_id) {
    try {
      const response = await transport.delete(
        `${BASE_PATH}/api/views/${view_id}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting view:", error);
      throw error;
    }
  },

  async updateCurrentView(view_id) {
    try {
      const response = await transport.put(
        `${BASE_PATH}/api/views/${view_id}/current_view`,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating current view:", error);
      throw error;
    }
  },

  async fetchContactDetails(conversation_id) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/v1/unibox/conversations/${conversation_id}/contact`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching contact details:", error);
      throw error;
    }
  },

  async fetchUniboxWhatsAppNumbers() {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/unibox/whatsapp_numbers`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching unibox WhatsApp numbers:", error);
      throw error;
    }
  },

  async saveUniboxWhatsAppNumbers(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/unibox/whatsapp_numbers`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error saving unibox WhatsApp numbers:", error);
      throw error;
    }
  },

  async addAsLead(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/unibox/add_as_lead`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error adding unknown conversation as lead:", error);
      throw error;
    }
  },

  async relinkEntity({ entityType, entityId, newEntityType, newEntityId, name }) {
    try {
      const encodedEntityId = encodeURIComponent(entityId);
      return await uniboxTransport.patch(
        `/api/v1/entities/${entityType}/${encodedEntityId}`,
        { new_entity_type: newEntityType, new_entity_id: newEntityId, name },
      );
    } catch (error) {
      console.error("Error relinking unibox entity:", error);
      throw error;
    }
  },

  async migrateMessages(entityType, entityId, payload) {
    const encodedEntityId = encodeURIComponent(entityId);
    return await uniboxTransport.post(
      `/api/v1/entities/${entityType}/${encodedEntityId}/migrate-messages`,
      payload,
    );
  },
};
